import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Table } from "@/types/table";
import { toast } from "sonner";

export type V2Shape = 'rect' | 'round' | 'square' | 'oval' | 'bar';

export interface V2Position {
  tableId: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  zoneId: string | null;
  isLocked: boolean;
  shape: V2Shape;
}

interface DbRow {
  table_id: string;
  pos_x_px: number | null;
  pos_y_px: number | null;
  width_px: number | null;
  height_px: number | null;
  rotation: number | null;
  zone_id: string | null;
  is_locked: boolean | null;
  shape: string | null;
}

const DEFAULT_W = 80;
const DEFAULT_H = 80;
const AUTO_SAVE_DELAY = 1500;

interface Options {
  establishmentId?: string;
  zoneId: string | null;
  autoSave?: boolean;
}

export const useFloorPlanV2 = (tables: Table[], { establishmentId, zoneId, autoSave = true }: Options) => {
  const [positions, setPositions] = useState<V2Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  const historyRef = useRef<V2Position[][]>([]);
  const futureRef = useRef<V2Position[][]>([]);
  const [, forceTick] = useState(0);
  const tick = () => forceTick(n => n + 1);

  const fetch = useCallback(async () => {
    if (!establishmentId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('floor_plan_positions')
        .select('table_id,pos_x_px,pos_y_px,width_px,height_px,rotation,zone_id,is_locked,shape')
        .eq('establishment_id', establishmentId);
      if (error) throw error;

      const byTable = new Map<string, DbRow>((data || []).map((r: any) => [r.table_id, r as DbRow]));
      const active = tables.filter(t => t.isActive);

      const built: V2Position[] = [];
      let cursorX = 16;
      let cursorY = 16;
      const ROW_LIMIT = 6;
      let rowCount = 0;

      for (const t of active) {
        const row = byTable.get(t.id);
        if (zoneId !== null && row && row.zone_id !== zoneId) continue;

        if (row && row.pos_x_px != null) {
          built.push({
            tableId: t.id,
            x: Number(row.pos_x_px),
            y: Number(row.pos_y_px ?? 0),
            width: Number(row.width_px ?? DEFAULT_W),
            height: Number(row.height_px ?? DEFAULT_H),
            rotation: Number(row.rotation ?? 0),
            zoneId: row.zone_id,
            isLocked: !!row.is_locked,
            shape: ((row.shape as V2Shape) || 'rect'),
          });
        } else if (zoneId !== null) {
          built.push({
            tableId: t.id,
            x: cursorX,
            y: cursorY,
            width: DEFAULT_W,
            height: DEFAULT_H,
            rotation: 0,
            zoneId,
            isLocked: false,
            shape: 'rect',
          });
          cursorX += DEFAULT_W + 16;
          rowCount++;
          if (rowCount >= ROW_LIMIT) { rowCount = 0; cursorX = 16; cursorY += DEFAULT_H + 16; }
        }
      }

      setPositions(built);
      historyRef.current = [];
      futureRef.current = [];
      setDirty(false);
    } catch (e) {
      console.error('[v2] fetch error', e);
      toast.error('Impossible de charger le plan');
    } finally {
      setLoading(false);
    }
  }, [establishmentId, zoneId, tables]);

  useEffect(() => { fetch(); }, [fetch]);

  const snapshot = useCallback(() => {
    historyRef.current.push(JSON.parse(JSON.stringify(positions)));
    if (historyRef.current.length > 50) historyRef.current.shift();
    futureRef.current = [];
    tick();
  }, [positions]);

  const updatePosition = useCallback((p: V2Position) => {
    setPositions(prev => prev.map(x => x.tableId === p.tableId ? p : x));
    setDirty(true);
  }, []);

  const updateMany = useCallback((updater: (prev: V2Position[]) => V2Position[]) => {
    setPositions(updater);
    setDirty(true);
  }, []);

  const undo = useCallback(() => {
    if (historyRef.current.length === 0) return;
    const prev = historyRef.current.pop()!;
    futureRef.current.push(JSON.parse(JSON.stringify(positions)));
    setPositions(prev);
    setDirty(true);
    tick();
  }, [positions]);

  const redo = useCallback(() => {
    if (futureRef.current.length === 0) return;
    const next = futureRef.current.pop()!;
    historyRef.current.push(JSON.parse(JSON.stringify(positions)));
    setPositions(next);
    setDirty(true);
    tick();
  }, [positions]);

  const save = useCallback(async () => {
    if (!establishmentId) return false;
    setSaving(true);
    try {
      const rows = positions.map(p => ({
        table_id: p.tableId,
        pos_x_px: Math.round(p.x),
        pos_y_px: Math.round(p.y),
        width_px: Math.round(p.width),
        height_px: Math.round(p.height),
        rotation: Math.round(p.rotation),
        zone_id: p.zoneId,
        is_locked: p.isLocked,
        shape: p.shape,
        establishment_id: establishmentId,
        x: Math.round(p.x / 80),
        y: Math.round(p.y / 80),
        width: Math.max(1, Math.round(p.width / 80)),
        height: Math.max(1, Math.round(p.height / 80)),
      }));
      const { error } = await supabase
        .from('floor_plan_positions')
        .upsert(rows, { onConflict: 'table_id' });
      if (error) throw error;
      setDirty(false);
      setLastSavedAt(new Date());
      tick();
      return true;
    } catch (e) {
      console.error('[v2] save error', e);
      toast.error('Échec de la sauvegarde');
      return false;
    } finally {
      setSaving(false);
    }
  }, [establishmentId, positions]);

  // Auto-save (debounced)
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!autoSave || !dirty || saving) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => { save(); }, AUTO_SAVE_DELAY);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  }, [dirty, saving, autoSave, save]);

  return {
    positions,
    loading,
    saving,
    dirty,
    lastSavedAt,
    snapshot,
    updatePosition,
    updateMany,
    undo,
    redo,
    canUndo: historyRef.current.length > 0,
    canRedo: futureRef.current.length > 0,
    save,
    refetch: fetch,
  };
};
